import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CameraIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { nutritionAPI } from '../../services/api'
import { isOutOfCredit } from '../../utils/apiError'
import { mealTimeOn } from '../../utils/eatToday'

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack']

function amountOf(item) {
  if (item.quantity != null) return `${Math.round(Number(item.quantity) * 10) / 10} ${item.quantity_unit ?? ''}`.trim()
  if (item.estimated_weight_g != null) return `${Math.round(Number(item.estimated_weight_g))} g`
  return ''
}

/**
 * VH-248: a photo or a line of text, checked, then logged. Calls exactly the endpoints the
 * Nutrition page uses (analyze-food-photo / save-food-photo-analysis, analyze-text /
 * save-text-entry) — no nutrition logic of its own. The meal is pre-selected from the clock (or
 * from the photo's time) and changed with one tap.
 */
export function QuickEntry({ date, defaultMealType, onLogged }) {
  const { t } = useTranslation()
  const fileInput = useRef(null)
  const [mode, setMode] = useState(null) // 'photo' | 'text' | null
  const [text, setText] = useState('')
  const [mealType, setMealType] = useState(defaultMealType)
  const [analysis, setAnalysis] = useState(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)

  const reset = () => { setMode(null); setText(''); setAnalysis(null); setError(null) }
  const fail = (err, key) => setError(isOutOfCredit(err) ? t('eat.outOfCredit') : t(key))

  const analysePhoto = async (file) => {
    if (!file) return
    setMode('photo'); setBusy(true); setError(null); setMessage(null); setAnalysis(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const { data } = await nutritionAPI.analyzeFoodPhoto(form)
      setAnalysis(data)
      if (data?.suggested_meal_type && MEAL_TYPES.includes(data.suggested_meal_type)) setMealType(data.suggested_meal_type)
    } catch (err) {
      fail(err, 'eat.analyseFailed')
    } finally {
      setBusy(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const analyseText = async () => {
    if (!text.trim()) return
    setBusy(true); setError(null); setMessage(null); setAnalysis(null)
    try {
      const { data } = await nutritionAPI.analyzeFoodText({
        description: text.trim(), meal_type: mealType, meal_time: mealTimeOn(date),
        log_date: date, mode: 'auto',
      })
      setAnalysis(data)
    } catch (err) {
      fail(err, 'eat.analyseFailed')
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    setBusy(true); setError(null)
    const common = { meal_type: mealType, meal_time: mealTimeOn(date), log_date: date }
    try {
      if (mode === 'photo') {
        await nutritionAPI.saveFoodPhotoAnalysis({ analysis_data: analysis, ...common })
      } else {
        await nutritionAPI.saveTextFoodEntry({
          analysis_data: { foods_identified: analysis.foods_identified, meal_total: analysis.meal_total }, ...common,
        })
      }
      const kcal = (analysis.foods_identified ?? []).reduce((s, f) => s + (Number(f.nutritional_summary?.calories) || 0), 0)
      setMessage(t('eat.logged', { meal: t(`nutrition.mealTypes.${mealType}`), kcal: Math.round(kcal) }))
      reset()
      onLogged?.()
    } catch (err) {
      fail(err, 'eat.saveFailed')
    } finally {
      setBusy(false)
    }
  }

  const foods = analysis?.foods_identified ?? []

  return (
    <section className="bg-surface-container rounded-lg border border-gray-200 p-4 space-y-3" data-testid="eat-quick-entry">
      <h2 className="text-base font-semibold text-gray-900">{t('eat.log')}</h2>

      <div className="grid grid-cols-2 gap-3">
        <button type="button" disabled={busy} data-testid="eat-photo"
          className="btn-primary flex items-center justify-center gap-2 py-3 text-base"
          onClick={() => fileInput.current?.click()}>
          <CameraIcon className="h-5 w-5" aria-hidden="true" />{t('eat.photo')}
        </button>
        <button type="button" disabled={busy} data-testid="eat-text"
          className="btn-outline flex items-center justify-center gap-2 py-3 text-base"
          onClick={() => { setMode('text'); setAnalysis(null); setMessage(null) }}>
          <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />{t('eat.type')}
        </button>
      </div>
      <input ref={fileInput} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid="eat-photo-input" onChange={(e) => analysePhoto(e.target.files?.[0])} />

      {mode === 'text' && !analysis && (
        <div className="space-y-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={500}
            placeholder={t('eat.typePlaceholder')} aria-label={t('eat.type')} data-testid="eat-text-input"
            className="form-input w-full text-base" />
          <button type="button" className="btn-primary w-full py-2" disabled={busy || !text.trim()}
            data-testid="eat-text-analyse" onClick={analyseText}>
            {busy ? t('eat.analysing') : t('eat.analyse')}
          </button>
        </div>
      )}
      {busy && mode === 'photo' && <p className="text-sm text-gray-600" role="status">{t('eat.analysing')}</p>}

      {analysis && (
        <div className="space-y-3" data-testid="eat-review">
          <ul className="divide-y divide-gray-100 text-sm">
            {foods.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-baseline justify-between gap-3 py-1.5">
                <span className="min-w-0 truncate text-gray-900">{f.name}
                  <span className="text-gray-500"> · {amountOf(f)}</span></span>
                <span className="tabular-nums text-gray-600 whitespace-nowrap">
                  {Math.round(Number(f.nutritional_summary?.calories) || 0)} kcal</span>
              </li>
            ))}
          </ul>
          {foods.length === 0 && <p className="text-sm text-amber-700">{t('eat.nothingFound')}</p>}
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('eat.mealType')}>
            {MEAL_TYPES.map((type) => (
              <button key={type} type="button" role="radio" aria-checked={mealType === type}
                data-testid={`eat-meal-${type}`} onClick={() => setMealType(type)}
                className={`rounded-full border px-3 py-1.5 text-sm ${mealType === type
                  ? 'border-teal-700 bg-teal-50 text-teal-900' : 'border-gray-300 text-gray-700'}`}>
                {t(`nutrition.mealTypes.${type}`)}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" className="btn-outline py-2" disabled={busy} onClick={reset}>{t('eat.cancel')}</button>
            <button type="button" className="btn-primary py-2" disabled={busy || foods.length === 0}
              data-testid="eat-save" onClick={save}>{t('eat.save')}</button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
      {message && <p className="text-sm text-green-800" role="status" data-testid="eat-message">{message}</p>}
    </section>
  )
}

export default QuickEntry
